import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  CircleAlert,
  CircleCheck,
  Mail,
  MailWarning,
  Paperclip,
  Plus,
  Send,
  Trash2,
  Upload,
} from 'lucide-react';
import {
  ACTION_TYPE_LABEL,
  CONTAINMENT_PLACE_LABEL,
  EIGHT_D_STEP_LABEL,
  RNC_ATTACHMENT_CATEGORY_LABEL,
  RNC_SEVERITY_LABEL,
  RNC_STATUS_LABEL,
  STANDARDIZATION_ITEM_LABEL,
  UserRole,
  WHYS_PER_TRACK,
  type ActionType,
  type ContainmentRowDto,
  type EightDDto,
  type EightDStep,
  type EmailListDto,
  type EmailMessageDto,
  type FileAccessDto,
  type NonconformityDto,
  type RncAttachmentCategory,
  type RncAttachmentDto,
  type StandardizationEntryDto,
  type TeamMemberDto,
} from '@weloze/shared';
import { api, ApiError } from '../lib/api';
import { useAuth } from '../lib/auth';
import { useConfirm } from '../components/ConfirmDialog';
import { FilePicker } from '../components/FilePicker';
import { usePageLabel } from '../lib/navigation';
import { formatDate, formatDateTime, formatQuantity } from '../lib/format';
import {
  Alert,
  Badge,
  Button,
  Card,
  cn,
  Field,
  Select,
  Skeleton,
  Textarea,
} from '../components/ui';

/**
 * `/rncs/:rncId` — a RNC e o 8D (PLAN §9, fatia i).
 *
 * **Uma tela só para os dois lados.** A Weloze emite e verifica; o fornecedor
 * contém, investiga e age. Duas telas para o mesmo formulário divergiriam no
 * primeiro campo novo, e ninguém saberia qual das duas o fornecedor está vendo
 * — que é justamente o que o portal veio resolver em relação ao e-mail.
 *
 * O que muda entre os papéis é **o que está habilitado**, não o que aparece: o
 * fornecedor lê o D7 e não o preenche, a Weloze lê o D2 e não o inventa. Esconder
 * seções faria cada lado achar que o formulário é menor do que é.
 */

/** Extensões que o servidor aceita. Espelha `files.service.ts`. */
const ACCEPT = '.pdf,.png,.jpg,.jpeg,.docx,.xlsx,.zip';

const SEVERITY_TONE = { MINOR: 'neutral', MEDIUM: 'warning', MAJOR: 'danger' } as const;

export function RncDetailPage() {
  const { rncId } = useParams();
  const { user } = useAuth();
  const isWeloze = user?.role !== UserRole.SUPPLIER_USER;
  const isAdmin = user?.role === UserRole.WELOZE_ADMIN;

  const [rnc, setRnc] = useState<NonconformityDto | null>(null);
  const [form, setForm] = useState<EightDDto | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  const load = useCallback(async () => {
    setError(null);
    try {
      const [umaRnc, oitoD] = await Promise.all([
        api.get<NonconformityDto>(`/nonconformities/${rncId}`),
        api.get<EightDDto>(`/nonconformities/${rncId}/eight-d`),
      ]);
      setRnc(umaRnc);
      setForm(oitoD);
      setDirty(false);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Não foi possível carregar a RNC.');
    }
  }, [rncId]);

  useEffect(() => {
    void load();
  }, [load]);

  usePageLabel(rnc ? `RNC ${rnc.number}` : null);

  /** Muda um campo do formulário e marca que há o que salvar. */
  const set = useCallback(<K extends keyof EightDDto>(campo: K, valor: EightDDto[K]) => {
    setForm((atual) => (atual ? { ...atual, [campo]: valor } : atual));
    setDirty(true);
  }, []);

  async function salvar() {
    if (!form) return;
    setSaving(true);
    setError(null);
    try {
      const salvo = await api.put<EightDDto>(`/nonconformities/${rncId}/eight-d`, {
        problemDescription: form.problemDescription,
        containmentMatrix: form.containmentMatrix,
        containmentPlan: form.containmentPlan,
        firstConformingLot: form.firstConformingLot,
        team: form.team,
        occurrenceWhys: form.occurrenceWhys,
        occurrenceRootCause: form.occurrenceRootCause,
        detectionWhys: form.detectionWhys,
        detectionRootCause: form.detectionRootCause,
        preventionWhys: form.preventionWhys,
        preventionRootCause: form.preventionRootCause,
        standardization: form.standardization,
        similarImpact: form.similarImpact,
        lessonsLearned: form.lessonsLearned,
        closureEvidence: form.closureEvidence,
        closureOwner: form.closureOwner,
      });
      setForm(salvo);
      setDirty(false);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Não foi possível salvar o 8D.');
    } finally {
      setSaving(false);
    }
  }

  async function enviar() {
    setSaving(true);
    setError(null);
    try {
      await salvar();
      setForm(await api.post<EightDDto>(`/nonconformities/${rncId}/eight-d/submit`));
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Não foi possível enviar o 8D.');
    } finally {
      setSaving(false);
    }
  }

  if (error && !rnc) return <Alert>{error}</Alert>;

  if (!rnc || !form) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-16 w-96" />
        <Skeleton className="h-32" />
        <Skeleton className="h-64" />
      </div>
    );
  }

  const encerrada = rnc.status === 'CLOSED' || rnc.status === 'CANCELLED';

  return (
    <div className="space-y-6 pb-24">
      <RncHeader rnc={rnc} />

      {isWeloze && <NotificationLine rnc={rnc} isAdmin={isAdmin} />}

      {error && <Alert>{error}</Alert>}

      {form.submittedAt && (
        <p className="flex items-center gap-2 text-sm text-emerald-700">
          <CircleCheck className="size-4" aria-hidden />
          8D enviado para análise em {formatDateTime(form.submittedAt)}
          {form.submittedByName && ` por ${form.submittedByName}`}.
        </p>
      )}

      <D1
        rnc={rnc}
        form={form}
        set={set}
        disabled={encerrada}
        storeId={rnc.storeId}
        isWeloze={isWeloze}
        onChanged={load}
      />
      <D2 form={form} set={set} disabled={encerrada} />
      <D3 form={form} set={set} disabled={encerrada} />
      <D4 form={form} set={set} disabled={encerrada} />
      <D5 rnc={rnc} form={form} isAdmin={isAdmin} isWeloze={isWeloze} disabled={encerrada} />
      <D6 form={form} set={set} disabled={encerrada} />
      <D7 rnc={rnc} form={form} isAdmin={isAdmin} encerrada={encerrada} onDone={load} />
      <D8
        form={form}
        set={set}
        disabled={encerrada}
        rncId={rnc.id}
        storeId={rnc.storeId}
        isWeloze={isWeloze}
        onChanged={load}
      />

      {/* Barra fixa: o 8D é longo, e um botão só no fim obrigaria a rolar a
          tela inteira para gravar o que foi digitado no começo. */}
      {!encerrada && (
        <div className="fixed inset-x-0 bottom-0 z-20 border-t border-slate-200 bg-white/95 backdrop-blur print:hidden">
          <div className="mx-auto flex max-w-7xl flex-wrap items-center justify-end gap-3 px-4 py-3 sm:px-6">
            {dirty && <span className="mr-auto text-sm text-amber-700">Há alterações não salvas.</span>}
            <Button variant="outline" loading={saving} onClick={() => void salvar()}>
              Salvar
            </Button>
            {/* O botão vale para os dois lados. Durante a transição a Qualidade
                transcreve 8D que chega por e-mail, e esconder o envio dela
                deixaria a resposta sem data enquanto a etapa vence — o rótulo é
                que muda, porque quem age é diferente. */}
            <Button loading={saving} onClick={() => void enviar()}>
              <Send className="size-4" aria-hidden />
              {isWeloze ? 'Registrar resposta do fornecedor' : 'Enviar para análise'}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------

function RncHeader({ rnc }: { rnc: NonconformityDto }) {
  return (
    <header className="space-y-2">
      <div className="flex flex-wrap items-center gap-2">
        <span className="rounded bg-slate-100 px-2 py-0.5 font-mono text-lg font-medium text-slate-800">
          {rnc.number}
        </span>
        <h1 className="text-2xl font-semibold tracking-tight text-slate-900">
          {rnc.supplierName}
        </h1>
        <span className="font-mono text-xs text-slate-500">{rnc.protheusRef}</span>
        <Badge tone={SEVERITY_TONE[rnc.severity]}>
          {RNC_SEVERITY_LABEL[rnc.severity]} · −{rnc.demeritPercent}%
        </Badge>
        <Badge tone={rnc.status === 'CLOSED' ? 'success' : 'neutral'}>
          {RNC_STATUS_LABEL[rnc.status]}
        </Badge>
      </div>

      <p className="text-sm text-slate-600">
        Emitida em {formatDate(rnc.issuedAt)}
        {rnc.productCode && ` · produto ${rnc.productCode}`}
        {rnc.productName && ` — ${rnc.productName}`}
        {rnc.invoiceRef && ` · NF ${rnc.invoiceRef}`}
        {rnc.productionOrder && ` · OP ${rnc.productionOrder}`}
        {/*
          As duas quantidades sempre juntas: é a razão entre elas que diz se o
          problema foi pontual ou se o lote inteiro está comprometido.
        */}
        {rnc.quantityAffected !== null &&
          ` · ${formatQuantity(rnc.quantityAffected)}${
            rnc.lotQuantity !== null ? ` de ${formatQuantity(rnc.lotQuantity)}` : ''
          } não conformes`}
        {rnc.quantityAffected === null &&
          rnc.lotQuantity !== null &&
          ` · lote de ${formatQuantity(rnc.lotQuantity)}`}
      </p>

      <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-slate-500">
        {rnc.stages.map((etapa) => (
          <span
            key={etapa.key}
            className={etapa.overdue ? 'flex items-center gap-1 text-red-600' : ''}
          >
            {etapa.overdue && <CircleAlert className="size-3" aria-hidden />}
            {etapa.label}:{' '}
            {etapa.doneAt
              ? formatDate(etapa.doneAt)
              : etapa.dueAt
                ? `prazo ${formatDate(etapa.dueAt)}`
                : '—'}
          </span>
        ))}
      </div>
    </header>
  );
}

/**
 * O fornecedor foi avisado?
 *
 * Só para a Weloze, e no topo: o prazo de contenção é de 24 h a partir da
 * emissão, e cobrar o prazo de quem nunca recebeu o aviso é o erro que esta
 * linha existe para impedir.
 *
 * O caso que motivou o botão de reenvio é concreto: loja sem contato cadastrado
 * no dia da emissão. Sem ele, cadastrar o contato depois não avisaria ninguém, e
 * a RNC seguiria correndo prazo em silêncio.
 */
function NotificationLine({ rnc, isAdmin }: { rnc: NonconformityDto; isAdmin: boolean }) {
  const [avisos, setAvisos] = useState<EmailMessageDto[] | null>(null);
  const [enviando, setEnviando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  const carregar = useCallback(async () => {
    try {
      const lista = await api.get<EmailListDto>(`/mail?nonconformityId=${rnc.id}&kind=RNC_ISSUED`);
      setAvisos(lista.messages);
    } catch {
      setAvisos([]);
    }
  }, [rnc.id]);

  useEffect(() => {
    void carregar();
  }, [carregar]);

  if (avisos === null) return null;

  const ultimo = avisos[0] ?? null;
  const entregue = ultimo && ultimo.status !== 'FAILED';

  async function reenviar() {
    setEnviando(true);
    setErro(null);
    try {
      await api.post(`/mail/rnc/${rnc.id}`);
      await carregar();
    } catch (err) {
      setErro(
        err instanceof ApiError
          ? err.message
          : 'Não foi possível reenviar. A loja tem contato cadastrado?',
      );
    } finally {
      setEnviando(false);
    }
  }

  return (
    <div className="print:hidden">
      <p
        className={cn(
          'flex flex-wrap items-center gap-x-2 gap-y-1 rounded-lg border px-3 py-2 text-xs',
          entregue
            ? 'border-slate-200 bg-slate-50/60 text-slate-600'
            : 'border-amber-200 bg-amber-50/60 text-amber-800',
        )}
      >
        {entregue ? (
          <Mail className="size-3.5 shrink-0" aria-hidden />
        ) : (
          <MailWarning className="size-3.5 shrink-0" aria-hidden />
        )}
        {ultimo === null ? (
          <span>
            <strong className="font-medium">Nenhum aviso saiu para o fornecedor.</strong> O prazo de
            contenção corre a partir da emissão.
          </span>
        ) : ultimo.status === 'FAILED' ? (
          <span>
            <strong className="font-medium">O aviso falhou</strong> em{' '}
            {formatDateTime(ultimo.sentAt)}
            {ultimo.error && ` — ${ultimo.error}`}
          </span>
        ) : (
          <span>
            Fornecedor avisado em {formatDateTime(ultimo.sentAt)}
            {ultimo.status === 'DRY_RUN' && ' (modo de ensaio: o e-mail não saiu)'} ·{' '}
            {ultimo.recipients.join(', ')}
          </span>
        )}
        {isAdmin && (
          <button
            type="button"
            disabled={enviando}
            onClick={() => void reenviar()}
            className="ml-auto underline-offset-4 hover:underline disabled:opacity-50"
          >
            {enviando ? 'enviando…' : ultimo ? 'reenviar' : 'enviar agora'}
          </button>
        )}
      </p>
      {erro && <Alert>{erro}</Alert>}
    </div>
  );
}

function Step({
  step,
  hint,
  children,
}: {
  step: EightDStep;
  hint: string;
  children: React.ReactNode;
}) {
  return (
    <Card className="break-inside-avoid">
      <h2 className="font-medium text-slate-900">{EIGHT_D_STEP_LABEL[step]}</h2>
      <p className="mt-1 text-sm text-slate-600">{hint}</p>
      <div className="mt-4 space-y-4">{children}</div>
    </Card>
  );
}

type Setter = <K extends keyof EightDDto>(campo: K, valor: EightDDto[K]) => void;

interface StepProps {
  form: EightDDto;
  set: Setter;
  disabled: boolean;
}

// --- D1 ---------------------------------------------------------------------

function D1({
  rnc,
  form,
  set,
  disabled,
  storeId,
  isWeloze,
  onChanged,
}: StepProps & {
  rnc: NonconformityDto;
  storeId: string;
  isWeloze: boolean;
  onChanged: () => void;
}) {
  return (
    <Step
      step="D1"
      hint="O que a Weloze encontrou, e como o fornecedor entendeu o problema. As fotos da situação aprovada e da reprovada entram aqui."
    >
      <div className="rounded-lg border border-slate-200 bg-slate-50/60 p-3">
        <p className="text-xs font-medium uppercase tracking-wide text-slate-500">
          Descrição da Weloze
        </p>
        <p className="mt-1 whitespace-pre-wrap text-sm text-slate-700">{rnc.description}</p>
      </div>

      <Textarea
        label="Como o fornecedor descreve o problema"
        name="problemDescription"
        rows={4}
        disabled={disabled}
        value={form.problemDescription ?? ''}
        onChange={(e) => set('problemDescription', e.target.value)}
      />

      <Attachments
        step="D1"
        form={form}
        storeId={storeId}
        rncId={rnc.id}
        disabled={disabled}
        isWeloze={isWeloze}
        onChanged={onChanged}
        categorize
      />
    </Step>
  );
}

// --- D2 ---------------------------------------------------------------------

const CONTAINMENT_COLUMNS = [
  { key: 'quantity', label: 'Qtde' },
  { key: 'inspected', label: 'Inspeção 100%' },
  { key: 'reworked', label: 'Retrabalhada' },
  { key: 'approved', label: 'Aprovada' },
  { key: 'rejected', label: 'Reprovada' },
] as const;

function D2({ form, set, disabled }: StepProps) {
  function muda(place: string, coluna: string, valor: string) {
    set(
      'containmentMatrix',
      form.containmentMatrix.map((linha) =>
        linha.place === place
          ? { ...linha, [coluna]: valor === '' ? null : Number(valor) }
          : linha,
      ) as ContainmentRowDto[],
    );
  }

  return (
    <Step
      step="D2"
      hint="Onde está o material suspeito e o que já foi feito em cada ponto. Em branco não é zero: zero peça no cliente é uma boa notícia apurada, em branco é uma coluna que ninguém foi conferir."
    >
      <div className="overflow-x-auto rounded-lg border border-slate-200">
        <table className="w-full min-w-[40rem] text-sm">
          <thead>
            <tr className="border-b border-slate-200 bg-slate-50/70 text-left text-xs uppercase tracking-wide text-slate-500">
              <th className="px-3 py-2 font-medium">Onde</th>
              {CONTAINMENT_COLUMNS.map((coluna) => (
                <th key={coluna.key} className="px-3 py-2 font-medium">
                  {coluna.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {form.containmentMatrix.map((linha) => (
              <tr key={linha.place}>
                <td className="px-3 py-2 text-slate-700">
                  {CONTAINMENT_PLACE_LABEL[linha.place]}
                </td>
                {CONTAINMENT_COLUMNS.map((coluna) => (
                  <td key={coluna.key} className="px-3 py-2">
                    <input
                      type="number"
                      min={0}
                      inputMode="numeric"
                      aria-label={`${CONTAINMENT_PLACE_LABEL[linha.place]} — ${coluna.label}`}
                      disabled={disabled}
                      value={linha[coluna.key] ?? ''}
                      onChange={(e) => muda(linha.place, coluna.key, e.target.value)}
                      className="w-24 rounded-md border border-slate-300 px-2 py-1 text-sm tabular-nums disabled:bg-slate-50"
                    />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Textarea
        label="Como conter em cada ponto"
        name="containmentPlan"
        rows={3}
        disabled={disabled}
        value={form.containmentPlan ?? ''}
        onChange={(e) => set('containmentPlan', e.target.value)}
      />
      {/*
        Descrição do **método**, e não um número de lote. Quem recebe a peça
        precisa saber como reconhecer, na doca, o material já depurado: etiqueta
        verde, carimbo na embalagem, nova numeração de lote. Um campo de uma
        linha convidava a escrever "L-2026-0342", que não diz nada a quem confere.
      */}
      <Textarea
        label="Como o primeiro lote conforme será identificado"
        name="firstConformingLot"
        rows={2}
        disabled={disabled}
        placeholder="Ex.: etiqueta verde “PÓS-AÇÃO” na caixa e novo número de lote a partir do L-2026-0342."
        hint="É o que separa, na doca da Weloze, o material suspeito do material bom."
        value={form.firstConformingLot ?? ''}
        onChange={(e) => set('firstConformingLot', e.target.value)}
      />

      {/*
        A data é do servidor, carimbada no primeiro salvamento em que a matriz
        ganha número. É ela que permite verificar o prazo de 24 h — e uma data
        digitada por quem precisa provar que cumpriu o prazo não prova nada.
      */}
      {form.containmentFilledAt && (
        <p className="text-xs text-slate-500">
          Contenção quantificada em {formatDateTime(form.containmentFilledAt)}.
        </p>
      )}
    </Step>
  );
}

// --- D3 ---------------------------------------------------------------------

function D3({ form, set, disabled }: StepProps) {
  function muda(index: number, campo: keyof TeamMemberDto, valor: string) {
    set(
      'team',
      form.team.map((membro, i) => (i === index ? { ...membro, [campo]: valor } : membro)),
    );
  }

  return (
    <Step step="D3" hint="Quem participou da análise.">
      {form.team.length === 0 && (
        <p className="text-sm text-slate-500">Nenhum participante registrado.</p>
      )}

      {form.team.map((membro, index) => (
        <div key={index} className="flex flex-wrap items-end gap-2">
          <Field
            label={index === 0 ? 'Nome' : ''}
            name={`team-name-${index}`}
            className="min-w-[12rem] flex-1"
            disabled={disabled}
            value={membro.name}
            onChange={(e) => muda(index, 'name', e.target.value)}
          />
          <Field
            label={index === 0 ? 'Função' : ''}
            name={`team-role-${index}`}
            className="min-w-[10rem] flex-1"
            disabled={disabled}
            value={membro.role}
            onChange={(e) => muda(index, 'role', e.target.value)}
          />
          <Field
            label={index === 0 ? 'Departamento' : ''}
            name={`team-dept-${index}`}
            className="min-w-[10rem] flex-1"
            disabled={disabled}
            value={membro.department}
            onChange={(e) => muda(index, 'department', e.target.value)}
          />
          <Button
            variant="ghost"
            size="sm"
            disabled={disabled}
            aria-label={`Remover ${membro.name || 'participante'}`}
            onClick={() => set('team', form.team.filter((_, i) => i !== index))}
          >
            <Trash2 className="size-4" aria-hidden />
          </Button>
        </div>
      ))}

      <Button
        variant="outline"
        size="sm"
        disabled={disabled}
        onClick={() => set('team', [...form.team, { name: '', role: '', department: '' }])}
      >
        <Plus className="size-4" aria-hidden />
        Adicionar participante
      </Button>
    </Step>
  );
}

// --- D4 ---------------------------------------------------------------------

function WhyTrack({
  titulo,
  explicacao,
  whys,
  rootCause,
  disabled,
  onWhys,
  onRootCause,
}: {
  titulo: string;
  explicacao: string;
  whys: string[];
  rootCause: string | null;
  disabled: boolean;
  onWhys: (valor: string[]) => void;
  onRootCause: (valor: string) => void;
}) {
  return (
    <div className="rounded-lg border border-slate-200 p-3">
      <p className="font-medium text-slate-800">{titulo}</p>
      <p className="mt-0.5 text-xs text-slate-500">{explicacao}</p>

      <div className="mt-3 space-y-2">
        {Array.from({ length: WHYS_PER_TRACK }, (_, index) => (
          <Field
            key={index}
            label={`${index + 1}º porquê`}
            name={`${titulo}-why-${index}`}
            disabled={disabled}
            value={whys[index] ?? ''}
            onChange={(e) =>
              onWhys(whys.map((texto, i) => (i === index ? e.target.value : texto)))
            }
          />
        ))}
      </div>

      <div className="mt-3">
        <Textarea
          label="Causa raiz"
          name={`${titulo}-root`}
          rows={2}
          disabled={disabled}
          value={rootCause ?? ''}
          onChange={(e) => onRootCause(e.target.value)}
        />
      </div>
    </div>
  );
}

function D4({ form, set, disabled }: StepProps) {
  return (
    <Step
      step="D4"
      hint="Três trilhas, e são falhas diferentes: a peça ruim foi feita, passou pela inspeção e, antes das duas, o risco nem tinha sido previsto. Ocorrência e detecção são obrigatórias para enviar."
    >
      <div className="grid gap-4 lg:grid-cols-3">
        <WhyTrack
          titulo="Ocorrência"
          explicacao="Por que o problema aconteceu."
          whys={form.occurrenceWhys}
          rootCause={form.occurrenceRootCause}
          disabled={disabled}
          onWhys={(valor) => set('occurrenceWhys', valor)}
          onRootCause={(valor) => set('occurrenceRootCause', valor)}
        />
        <WhyTrack
          titulo="Detecção"
          explicacao="Por que ele não foi detectado antes de sair."
          whys={form.detectionWhys}
          rootCause={form.detectionRootCause}
          disabled={disabled}
          onWhys={(valor) => set('detectionWhys', valor)}
          onRootCause={(valor) => set('detectionRootCause', valor)}
        />
        {/*
          A trilha que o RQ 002-04 pede e faltava aqui. Ocorrência e detecção
          olham para o dia em que a peça foi feita; a prevenção olha para antes
          disso — o defeito era previsível, e o PFMEA não o previu. Sem ela o 8D
          conserta o processo e deixa o método de análise de risco intacto.
        */}
        <WhyTrack
          titulo="Prevenção"
          explicacao="Por que o PFMEA não previu a possibilidade deste defeito."
          whys={form.preventionWhys}
          rootCause={form.preventionRootCause}
          disabled={disabled}
          onWhys={(valor) => set('preventionWhys', valor)}
          onRootCause={(valor) => set('preventionRootCause', valor)}
        />
      </div>
    </Step>
  );
}

// --- D5 ---------------------------------------------------------------------

interface PlanoDto {
  id: string;
  description: string;
  dueAt: string | null;
  status: string;
  response: string | null;
  fileId: string | null;
  actionType: ActionType | null;
  owner: string | null;
}

const PLANO_STATUS: Array<{ valor: string; rotulo: string }> = [
  { valor: 'OPEN', rotulo: 'Em aberto' },
  { valor: 'IN_PROGRESS', rotulo: 'Em andamento' },
  { valor: 'DONE', rotulo: 'Concluída' },
];

/**
 * O D5 não guarda as ações — ele **mostra** as que existem.
 *
 * Elas são `ActionPlan` com origem `RNC`, a mesma entidade do plano do semestre,
 * com o mesmo acompanhamento de prazo. Uma tabela própria aqui seria uma segunda
 * implementação de algo que já funciona, e as duas listas divergiriam.
 *
 * ## Quem preenche é o FORNECEDOR
 *
 * Até 09/2026 esta seção inteira estava dentro de um `isAdmin`, e as duas rotas
 * eram `requireAdmin`: na prática, a Weloze escrevia as ações do fornecedor.
 * É o oposto do que o `RQ 002-04` pede — quem sabe o que fazer para a causa não
 * voltar é quem produz.
 *
 * A Weloze continua podendo lançar ação, para quando o fornecedor responde por
 * outro canal ou a resposta é inadequada. A diferença entre os dois lados é o
 * que cada um **altera depois**: o fornecedor registra andamento (situação, o
 * que foi feito, evidência); descrição, tipo, responsável e prazo são da Weloze,
 * porque "quem combinou este prazo?" precisa ter uma resposta só. O servidor
 * recusa o resto — isto aqui é só a tela correspondente.
 */
function D5({
  rnc,
  form,
  isAdmin,
  isWeloze,
  disabled,
}: {
  rnc: NonconformityDto;
  form: EightDDto;
  isAdmin: boolean;
  isWeloze: boolean;
  disabled: boolean;
}) {
  const [planos, setPlanos] = useState<PlanoDto[] | null>(null);
  const [criando, setCriando] = useState(false);
  const [descricao, setDescricao] = useState('');
  const [prazo, setPrazo] = useState('');
  const [tipo, setTipo] = useState<ActionType>('CORRECTIVE');
  const [responsavel, setResponsavel] = useState('');
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  // Quem pode lançar ação: o fornecedor (é dele o D5) e o administrador da
  // Weloze. O perfil de consulta da Weloze lê e não escreve, como em toda tela.
  const podeLancar = !disabled && (isAdmin || !isWeloze);

  const carregar = useCallback(async () => {
    try {
      setPlanos(await api.get<PlanoDto[]>(`/nonconformities/${rnc.id}/action-plans`));
    } catch {
      setPlanos([]);
    }
  }, [rnc.id]);

  useEffect(() => {
    void carregar();
  }, [carregar]);

  async function lancar() {
    setSalvando(true);
    setErro(null);
    try {
      await api.post(`/stores/${rnc.storeId}/action-plans`, {
        origin: 'RNC',
        nonconformityId: rnc.id,
        description: descricao,
        dueAt: prazo || null,
        actionType: tipo,
        owner: responsavel.trim() || null,
      });
      setDescricao('');
      setPrazo('');
      setResponsavel('');
      setCriando(false);
      await carregar();
    } catch (err) {
      setErro(err instanceof ApiError ? err.message : 'Não foi possível registrar a ação.');
    } finally {
      setSalvando(false);
    }
  }

  return (
    <Step
      step="D5"
      hint={
        isWeloze
          ? 'As ações são do fornecedor — contenção, abrangência, detecção e corretiva, cada uma com responsável e prazo. Lance por aqui apenas o que ele informar por outro canal, ou o que faltar na resposta dele.'
          : 'O que a sua empresa vai fazer para o problema não voltar: contenção, abrangência, detecção e ação corretiva, cada uma com responsável e prazo. Registre aqui e depois vá marcando o andamento com a evidência.'
      }
    >
      {erro && <Alert>{erro}</Alert>}

      {planos === null ? (
        <Skeleton className="h-12" />
      ) : planos.length === 0 ? (
        <p className="text-sm text-slate-600">
          {isWeloze
            ? 'Nenhuma ação registrada nesta RNC.'
            : 'Nenhuma ação registrada ainda. É este o campo em que a sua empresa responde o que vai fazer.'}
        </p>
      ) : (
        <ul className="divide-y divide-slate-100">
          {planos.map((plano) => (
            <Acao
              key={plano.id}
              plano={plano}
              storeId={rnc.storeId}
              disabled={disabled}
              onChanged={carregar}
            />
          ))}
        </ul>
      )}

      {podeLancar && !criando && (
        <Button variant="outline" size="sm" onClick={() => setCriando(true)}>
          <Plus className="size-4" aria-hidden />
          {isWeloze ? 'Solicitar ação' : 'Registrar ação'}
        </Button>
      )}

      {podeLancar && criando && (
        <div className="space-y-3 rounded-lg border border-slate-200 p-3">
          <Textarea
            label={isWeloze ? 'O que precisa ser feito' : 'O que a sua empresa vai fazer'}
            name="acao-descricao"
            rows={2}
            value={descricao}
            onChange={(e) => setDescricao(e.target.value)}
          />
          <div className="grid gap-3 sm:grid-cols-3">
            <Select
              label="Tipo de ação"
              name="acao-tipo"
              value={tipo}
              onChange={(e) => setTipo(e.target.value as ActionType)}
            >
              {(Object.keys(ACTION_TYPE_LABEL) as ActionType[]).map((valor) => (
                <option key={valor} value={valor}>
                  {ACTION_TYPE_LABEL[valor]}
                </option>
              ))}
            </Select>
            {/*
              A equipe do D3 sugere, mas não limita: quem responde por uma ação
              de abrangência pode ser alguém que nem participou da análise. Daí
              `datalist` e não `select` — lista quem já está no formulário e
              aceita um nome novo sem obrigar a cadastrá-lo na equipe.
            */}
            <Field
              label="Responsável"
              name="acao-responsavel"
              list="acao-equipe"
              placeholder="Nome de quem responde"
              value={responsavel}
              onChange={(e) => setResponsavel(e.target.value)}
            />
            <datalist id="acao-equipe">
              {form.team
                .filter((membro) => membro.name.trim())
                .map((membro) => (
                  <option key={membro.name} value={membro.name}>
                    {membro.role || membro.department}
                  </option>
                ))}
            </datalist>
            <Field
              label="Prazo"
              name="acao-prazo"
              type="date"
              value={prazo}
              onChange={(e) => setPrazo(e.target.value)}
            />
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="ghost" size="sm" onClick={() => setCriando(false)}>
              Cancelar
            </Button>
            <Button
              size="sm"
              loading={salvando}
              disabled={!descricao.trim()}
              onClick={() => void lancar()}
            >
              Registrar
            </Button>
          </div>
        </div>
      )}
    </Step>
  );
}

/**
 * Uma ação, com o andamento.
 *
 * O andamento abre em linha, e não em modal: são várias ações por RNC, e um
 * diálogo por ação obrigaria a fechar e reabrir para comparar o que já foi
 * feito com o que falta.
 */
function Acao({
  plano,
  storeId,
  disabled,
  onChanged,
}: {
  plano: PlanoDto;
  storeId: string;
  disabled: boolean;
  onChanged: () => Promise<void>;
}) {
  const [aberto, setAberto] = useState(false);
  const [status, setStatus] = useState(plano.status);
  const [resposta, setResposta] = useState(plano.response ?? '');
  const [fileId, setFileId] = useState<string | null>(plano.fileId);
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  async function salvar() {
    setSalvando(true);
    setErro(null);
    try {
      // Só os três campos do andamento. Mandar o resto faria o servidor recusar
      // a chamada inteira do fornecedor, e por um campo que a tela nem mostra.
      await api.put(`/action-plans/${plano.id}`, {
        status,
        response: resposta.trim() || null,
        fileId,
      });
      setAberto(false);
      await onChanged();
    } catch (err) {
      setErro(err instanceof ApiError ? err.message : 'Não foi possível salvar o andamento.');
    } finally {
      setSalvando(false);
    }
  }

  return (
    <li className="py-2 text-sm">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <span className="min-w-0 flex-1 text-slate-800">{plano.description}</span>
        {plano.actionType && <Badge tone="brand">{ACTION_TYPE_LABEL[plano.actionType]}</Badge>}
        <Badge
          tone={
            plano.status === 'DONE' ? 'success' : plano.status === 'OVERDUE' ? 'danger' : 'neutral'
          }
        >
          {plano.status === 'DONE'
            ? 'concluída'
            : plano.status === 'OVERDUE'
              ? 'prazo vencido'
              : plano.status === 'IN_PROGRESS'
                ? 'em andamento'
                : 'em aberto'}
        </Badge>
      </div>

      {(plano.owner || plano.dueAt) && (
        <p className="text-xs text-slate-500">
          {plano.owner && `responsável ${plano.owner}`}
          {plano.owner && plano.dueAt && ' · '}
          {plano.dueAt && `prazo ${formatDate(plano.dueAt)}`}
        </p>
      )}

      {plano.response && !aberto && (
        <p className="mt-1 rounded-lg bg-slate-50 px-3 py-2 text-sm text-slate-700">
          {plano.response}
        </p>
      )}

      <div className="mt-1 flex flex-wrap items-center gap-3">
        {plano.fileId && (
          <a
            href={`/api/files/${plano.fileId}`}
            className="inline-flex items-center gap-1 text-xs text-brand-700 underline-offset-4 hover:underline"
          >
            <Paperclip className="size-3" aria-hidden />
            evidência
          </a>
        )}
        {!disabled && !aberto && (
          <button
            type="button"
            onClick={() => setAberto(true)}
            className="text-xs text-slate-500 underline-offset-4 hover:text-slate-800 hover:underline"
          >
            {plano.response ? 'Editar andamento' : 'Registrar andamento'}
          </button>
        )}
      </div>

      {aberto && (
        <div className="mt-2 space-y-3 rounded-lg border border-slate-200 p-3">
          {erro && <Alert>{erro}</Alert>}
          <Select
            label="Situação"
            name={`acao-status-${plano.id}`}
            className="w-52"
            value={status}
            onChange={(e) => setStatus(e.target.value)}
          >
            {PLANO_STATUS.map((opcao) => (
              <option key={opcao.valor} value={opcao.valor}>
                {opcao.rotulo}
              </option>
            ))}
          </Select>
          <Textarea
            label="O que foi feito"
            name={`acao-resposta-${plano.id}`}
            rows={2}
            value={resposta}
            onChange={(e) => setResposta(e.target.value)}
          />
          <FilePicker
            storeId={storeId}
            label="Evidência"
            value={fileId}
            onChange={(id: string | null) => setFileId(id)}
          />
          <div className="flex justify-end gap-2">
            <Button variant="ghost" size="sm" onClick={() => setAberto(false)}>
              Cancelar
            </Button>
            <Button size="sm" loading={salvando} onClick={() => void salvar()}>
              Salvar andamento
            </Button>
          </div>
        </div>
      )}
    </li>
  );
}

// --- D6 ---------------------------------------------------------------------

function D6({ form, set, disabled }: StepProps) {
  function muda(item: string, mudanca: Partial<StandardizationEntryDto>) {
    set(
      'standardization',
      form.standardization.map((entrada) =>
        entrada.item === item ? { ...entrada, ...mudanca } : entrada,
      ),
    );
  }

  return (
    <Step
      step="D6"
      hint="O que precisa ser atualizado para o problema não voltar. Em branco é diferente de “não se aplica”: um responde que ninguém olhou ainda."
    >
      <div className="overflow-x-auto rounded-lg border border-slate-200">
        <table className="w-full min-w-[32rem] text-sm">
          <thead>
            <tr className="border-b border-slate-200 bg-slate-50/70 text-left text-xs uppercase tracking-wide text-slate-500">
              <th className="px-3 py-2 font-medium">Documento</th>
              <th className="px-3 py-2 font-medium">Aplica?</th>
              <th className="px-3 py-2 font-medium">Prazo</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {form.standardization.map((entrada) => (
              <tr key={entrada.item}>
                <td className="px-3 py-2 text-slate-700">
                  {STANDARDIZATION_ITEM_LABEL[entrada.item]}
                </td>
                <td className="px-3 py-2">
                  <select
                    aria-label={`${STANDARDIZATION_ITEM_LABEL[entrada.item]} — aplica?`}
                    disabled={disabled}
                    value={entrada.applies === null ? '' : entrada.applies ? 'S' : 'N'}
                    onChange={(e) =>
                      muda(entrada.item, {
                        applies: e.target.value === '' ? null : e.target.value === 'S',
                      })
                    }
                    className="rounded-md border border-slate-300 px-2 py-1 text-sm disabled:bg-slate-50"
                  >
                    <option value="">—</option>
                    <option value="S">Sim</option>
                    <option value="N">Não</option>
                  </select>
                </td>
                <td className="px-3 py-2">
                  <input
                    type="date"
                    aria-label={`${STANDARDIZATION_ITEM_LABEL[entrada.item]} — prazo`}
                    disabled={disabled || entrada.applies !== true}
                    value={entrada.dueAt ?? ''}
                    onChange={(e) => muda(entrada.item, { dueAt: e.target.value || null })}
                    className="rounded-md border border-slate-300 px-2 py-1 text-sm disabled:bg-slate-50"
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/*
        A pergunta que transforma a correção de um item na correção de uma
        família. O mesmo dispositivo, a mesma matriz-prima e o mesmo turno
        atendem outras peças — e se o problema é do processo, ele já está nelas.
      */}
      <Textarea
        label="Impacto em produtos ou processos similares"
        name="similarImpact"
        rows={3}
        disabled={disabled}
        hint="Quais outros itens passam pelo mesmo processo, e o que foi verificado neles."
        value={form.similarImpact ?? ''}
        onChange={(e) => set('similarImpact', e.target.value)}
      />
      <Textarea
        label="Lições aprendidas"
        name="lessonsLearned"
        rows={3}
        disabled={disabled}
        hint="O que a sua equipe leva deste caso para os próximos."
        value={form.lessonsLearned ?? ''}
        onChange={(e) => set('lessonsLearned', e.target.value)}
      />
    </Step>
  );
}

// --- D7 ---------------------------------------------------------------------

/**
 * O veredito da Weloze.
 *
 * O fornecedor **vê e não preenche**. Esconder a seção dele seria pior: ele
 * precisa saber que existe uma verificação, e ler o motivo quando a ação for
 * recusada.
 *
 * **Registrar a eficácia não encerra a RNC** (decisão do Vinicius, 09/2026).
 * Verificar e encerrar são atos diferentes: às vezes a ação foi eficaz e a
 * Qualidade ainda quer acompanhar o próximo lote antes de fechar. Encerrar tem
 * botão próprio — e o servidor recusa fechar sem eficácia confirmada, porque
 * arquivar ação reprovada faria o histórico dizer que o problema foi resolvido.
 */
function D7({
  rnc,
  form,
  isAdmin,
  encerrada,
  onDone,
}: {
  rnc: NonconformityDto;
  form: EightDDto;
  isAdmin: boolean;
  encerrada: boolean;
  onDone: () => void;
}) {
  const confirm = useConfirm();
  const [implementadas, setImplementadas] = useState(form.actionsImplemented ?? true);
  const [eficaz, setEficaz] = useState(form.effective ?? true);
  const [motivoNaoImplementadas, setMotivoNaoImplementadas] = useState(
    form.actionsImplementedNote ?? '',
  );
  const [justificativa, setJustificativa] = useState(rnc.effectivenessRecord ?? '');
  const [responsavel, setResponsavel] = useState(form.effectivenessOwner ?? '');
  const [erro, setErro] = useState<string | null>(null);
  const [salvando, setSalvando] = useState(false);

  // Depois de gravar, o formulário volta a mostrar o que ficou registrado. Sem
  // isso, revisar uma segunda vez partiria dos valores antigos da tela.
  useEffect(() => {
    setImplementadas(form.actionsImplemented ?? true);
    setEficaz(form.effective ?? true);
    setMotivoNaoImplementadas(form.actionsImplementedNote ?? '');
    setResponsavel(form.effectivenessOwner ?? '');
  }, [
    form.reviewedAt,
    form.actionsImplemented,
    form.actionsImplementedNote,
    form.effective,
    form.effectivenessOwner,
  ]);

  async function registrar() {
    setSalvando(true);
    setErro(null);
    try {
      await api.post(`/nonconformities/${rnc.id}/eight-d/review`, {
        actionsImplemented: implementadas,
        actionsImplementedNote: motivoNaoImplementadas || null,
        effective: eficaz,
        justification: justificativa || null,
        effectivenessOwner: responsavel,
      });
      onDone();
    } catch (err) {
      setErro(err instanceof ApiError ? err.message : 'Não foi possível registrar a eficácia.');
    } finally {
      setSalvando(false);
    }
  }

  function encerrar() {
    confirm.ask({
      title: `Encerrar a RNC ${rnc.number}`,
      body: (
        <>
          <p>
            A RNC passa a <strong>encerrada</strong> e sai da lista de tratativas do fornecedor.
            O 8D fica somente para leitura.
          </p>
          <p>
            O demérito já contado na competência da emissão <strong>não muda</strong>: encerrar
            registra que o problema foi tratado, não que ele deixou de ter acontecido.
          </p>
        </>
      ),
      confirmLabel: 'Encerrar RNC',
      destructive: false,
      onConfirm: async () => {
        await api.post(`/nonconformities/${rnc.id}/close`);
        onDone();
      },
    });
  }

  const jaAvaliado = form.effective !== null;

  return (
    <Step
      step="D7"
      hint="Quem age não é quem verifica: esta seção é da Weloze. Registrar a eficácia e encerrar a RNC são dois atos — o segundo tem botão próprio."
    >
      {confirm.dialog}

      {jaAvaliado && (
        <div className="space-y-1 rounded-lg border border-slate-200 bg-slate-50/60 p-3 text-sm">
          <p className="text-slate-700">
            Ações implementadas: <strong>{form.actionsImplemented ? 'sim' : 'não'}</strong> ·
            eficácia:{' '}
            <strong className={form.effective ? 'text-emerald-700' : 'text-red-700'}>
              {form.effective ? 'confirmada' : 'não confirmada'}
            </strong>
          </p>
          {form.effectivenessOwner && (
            <p className="text-slate-600">
              Verificado por {form.effectivenessOwner}
              {form.reviewedAt && ` em ${formatDateTime(form.reviewedAt)}`}.
            </p>
          )}
          {form.actionsImplementedNote && (
            <p className="whitespace-pre-wrap text-slate-700">
              <span className="text-slate-500">Por que não foram implementadas: </span>
              {form.actionsImplementedNote}
            </p>
          )}
          {rnc.effectivenessRecord && (
            <p className="whitespace-pre-wrap text-slate-700">
              <span className="text-slate-500">Por que não foi eficaz: </span>
              {rnc.effectivenessRecord}
            </p>
          )}
        </div>
      )}

      {!jaAvaliado && !isAdmin && (
        <p className="text-sm text-slate-600">
          A Weloze ainda não registrou a verificação de eficácia.
        </p>
      )}

      {isAdmin && !encerrada && (
        <div className={jaAvaliado ? 'space-y-4 print:hidden' : 'space-y-4'}>
          {erro && <Alert>{erro}</Alert>}
          <div className="flex flex-wrap gap-3">
            <Select
              label="Ações implementadas"
              name="implementadas"
              className="w-44"
              value={implementadas ? 'S' : 'N'}
              onChange={(e) => setImplementadas(e.target.value === 'S')}
            >
              <option value="S">Sim</option>
              <option value="N">Não</option>
            </Select>
            <Select
              label="Foram eficazes"
              name="eficaz"
              className="w-44"
              value={eficaz ? 'S' : 'N'}
              onChange={(e) => setEficaz(e.target.value === 'S')}
            >
              <option value="S">Sim</option>
              <option value="N">Não</option>
            </Select>
            <Field
              label="Verificado por"
              name="responsavel"
              className="min-w-[14rem] flex-1"
              value={responsavel}
              onChange={(e) => setResponsavel(e.target.value)}
            />
          </div>

          {/*
            O "não" sem motivo transforma o D7 num carimbo: daqui a seis meses
            ninguém sabe se a ação falhou, atrasou ou nunca começou. O servidor
            também exige — o campo existe aqui para a pessoa não descobrir a
            exigência pela mensagem de erro.
          */}
          {!implementadas && (
            <Textarea
              label="Por que as ações não foram implementadas"
              name="motivoNaoImplementadas"
              rows={3}
              hint="Obrigatório."
              value={motivoNaoImplementadas}
              onChange={(e) => setMotivoNaoImplementadas(e.target.value)}
            />
          )}

          {!eficaz && (
            <Textarea
              label="Por que não foi eficaz"
              name="justificativa"
              rows={3}
              hint="Obrigatório. Recusar sem dizer o motivo não ensina nada a quem precisa agir."
              value={justificativa}
              onChange={(e) => setJustificativa(e.target.value)}
            />
          )}

          <div className="flex flex-wrap items-center gap-2">
            <Button loading={salvando} onClick={() => void registrar()}>
              {jaAvaliado ? 'Atualizar verificação' : 'Registrar verificação'}
            </Button>
            {form.effective === true && (
              <Button variant="outline" onClick={encerrar}>
                <CircleCheck className="size-4" aria-hidden />
                Encerrar RNC
              </Button>
            )}
            {form.effective === false && (
              <span className="text-sm text-slate-600">
                Encerrar exige eficácia confirmada — a RNC segue em tratativa.
              </span>
            )}
          </div>
        </div>
      )}
    </Step>
  );
}

// --- D8 ---------------------------------------------------------------------

function D8({
  form,
  set,
  disabled,
  rncId,
  storeId,
  isWeloze,
  onChanged,
}: StepProps & {
  rncId: string;
  storeId: string;
  isWeloze: boolean;
  onChanged: () => void;
}) {
  return (
    <Step step="D8" hint="As evidências do encerramento.">
      <Textarea
        label="Evidências"
        name="closureEvidence"
        rows={3}
        disabled={disabled}
        value={form.closureEvidence ?? ''}
        onChange={(e) => set('closureEvidence', e.target.value)}
      />
      <Field
        label="Responsável"
        name="closureOwner"
        disabled={disabled}
        value={form.closureOwner ?? ''}
        onChange={(e) => set('closureOwner', e.target.value)}
      />
      {/* Carimbada no primeiro salvamento com evidência, pelo mesmo motivo do D2. */}
      {form.closureAt && (
        <p className="text-xs text-slate-500">
          Evidências registradas em {formatDateTime(form.closureAt)}.
        </p>
      )}
      <Attachments
        step="D8"
        form={form}
        rncId={rncId}
        storeId={storeId}
        disabled={disabled}
        isWeloze={isWeloze}
        onChanged={onChanged}
      />
    </Step>
  );
}

// --- Anexos -----------------------------------------------------------------

/**
 * Anexos de uma disciplina.
 *
 * O envio é em dois passos: o arquivo sobe para a loja, e depois o id volta
 * vinculado à disciplina. É o que permite o mesmo acervo servir certificado,
 * documento de ficha e evidência de 8D sem uma rota de upload por consumidor.
 *
 * O download **não** é um link direto: passa pela rota autenticada, que confere
 * a loja dona e registra quem baixou.
 *
 * No D1 os anexos são **categorizados**: a foto da situação aprovada ao lado da
 * reprovada é o que transforma uma imagem de peça em evidência. Uma sozinha
 * mostra um defeito; as duas mostram a diferença. Nas demais disciplinas a
 * pergunta não existe, e o seletor não aparece.
 */
function Attachments({
  step,
  form,
  storeId,
  rncId,
  disabled,
  isWeloze,
  onChanged,
  categorize = false,
}: {
  step: EightDStep;
  form: EightDDto;
  storeId: string;
  rncId: string;
  disabled: boolean;
  isWeloze: boolean;
  onChanged: () => void;
  categorize?: boolean;
}) {
  const input = useRef<HTMLInputElement>(null);
  const confirm = useConfirm();
  const [enviando, setEnviando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const [categoria, setCategoria] = useState<RncAttachmentCategory>('REJECTED_SITUATION');

  const doPasso = useMemo(
    () => form.attachments.filter((anexo) => anexo.step === step),
    [form.attachments, step],
  );

  /**
   * Agrupado por categoria quando ela existe, na ordem do formulário: reprovada
   * primeiro, que é o que motivou a RNC.
   */
  const grupos = useMemo(() => {
    if (!categorize) return [{ categoria: null, anexos: doPasso }];
    const ordem: RncAttachmentCategory[] = ['REJECTED_SITUATION', 'APPROVED_SITUATION', 'OTHER'];
    return ordem
      .map((valor) => ({
        categoria: valor,
        anexos: doPasso.filter((anexo) => anexo.category === valor),
      }))
      .filter((grupo) => grupo.anexos.length > 0);
  }, [categorize, doPasso]);

  async function enviar(file: File) {
    setEnviando(true);
    setErro(null);
    try {
      const arquivo = await api.upload<{ id: string }>(`/stores/${storeId}/files`, file);
      await api.post(`/nonconformities/${rncId}/eight-d/attachments`, {
        fileId: arquivo.id,
        step,
        category: categorize ? categoria : 'OTHER',
      });
      onChanged();
    } catch (err) {
      setErro(err instanceof ApiError ? err.message : 'Não foi possível anexar o arquivo.');
    } finally {
      setEnviando(false);
      if (input.current) input.current.value = '';
    }
  }

  function remover(anexo: RncAttachmentDto) {
    confirm.ask({
      title: 'Remover anexo do 8D',
      body: (
        <>
          <p>
            <strong>{anexo.fileName}</strong> deixa de aparecer nesta disciplina.
          </p>
          <p>
            O arquivo <strong>continua no acervo</strong>, com o registro de quem já o baixou —
            apagar o binário destruiria a prova de que ele existiu e foi acessado.
          </p>
        </>
      ),
      confirmLabel: 'Remover anexo',
      onConfirm: async () => {
        await api.delete(`/rnc-attachments/${anexo.id}`);
        onChanged();
      },
    });
  }

  return (
    <div>
      {confirm.dialog}
      {erro && <Alert>{erro}</Alert>}

      {grupos.map((grupo) => (
        <div key={grupo.categoria ?? 'todos'} className="mb-2">
          {grupo.categoria && (
            <p className="text-xs font-medium uppercase tracking-wide text-slate-500">
              {RNC_ATTACHMENT_CATEGORY_LABEL[grupo.categoria]}
            </p>
          )}
          <ul className="space-y-1">
            {grupo.anexos.map((anexo) => (
              <li key={anexo.id} className="flex flex-wrap items-center gap-2 text-sm">
                <Paperclip className="size-4 shrink-0 text-slate-400" aria-hidden />
                <a
                  href={`/api/files/${anexo.fileId}`}
                  className="text-brand-700 underline-offset-4 hover:underline"
                >
                  {anexo.fileName}
                </a>
                <span className="text-xs text-slate-500">
                  {(anexo.sizeBytes / 1024).toLocaleString('pt-BR', { maximumFractionDigits: 0 })}{' '}
                  kB
                  {anexo.uploadedByName && ` · ${anexo.uploadedByName}`}
                </span>
                {isWeloze && <AccessLog fileId={anexo.fileId} />}
                {!disabled && (
                  <Button
                    variant="ghost"
                    size="sm"
                    aria-label={`Remover ${anexo.fileName}`}
                    onClick={() => remover(anexo)}
                  >
                    <Trash2 className="size-4" aria-hidden />
                  </Button>
                )}
              </li>
            ))}
          </ul>
        </div>
      ))}

      {!disabled && (
        <div className="flex flex-wrap items-end gap-2 print:hidden">
          <input
            ref={input}
            type="file"
            accept={ACCEPT}
            className="sr-only"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) void enviar(file);
            }}
          />
          {/*
            A categoria é escolhida ANTES de abrir o seletor de arquivo: o envio
            é imediato ao escolher, e um seletor depois pediria uma segunda ação
            que ninguém faria.
          */}
          {categorize && (
            <Select
              label="O anexo mostra"
              name={`categoria-${step}`}
              className="w-52"
              value={categoria}
              onChange={(e) => setCategoria(e.target.value as RncAttachmentCategory)}
            >
              {(Object.keys(RNC_ATTACHMENT_CATEGORY_LABEL) as RncAttachmentCategory[]).map(
                (valor) => (
                  <option key={valor} value={valor}>
                    {RNC_ATTACHMENT_CATEGORY_LABEL[valor]}
                  </option>
                ),
              )}
            </Select>
          )}
          <Button
            variant="outline"
            size="sm"
            loading={enviando}
            onClick={() => input.current?.click()}
          >
            <Upload className="size-4" aria-hidden />
            Anexar arquivo
          </Button>
        </div>
      )}
    </div>
  );
}

/**
 * Quem baixou este anexo.
 *
 * Só para a Weloze, e sob demanda: a lista responde "o fornecedor viu?" sem
 * depender de ninguém lembrar, e é o motivo de o download ser registrado. Fica
 * recolhida porque a pergunta é ocasional — quem abre o 8D quer o 8D.
 */
function AccessLog({ fileId }: { fileId: string }) {
  const [aberto, setAberto] = useState(false);
  const [acessos, setAcessos] = useState<FileAccessDto[] | null>(null);

  async function abrir() {
    setAberto(true);
    if (acessos) return;
    try {
      setAcessos(await api.get<FileAccessDto[]>(`/files/${fileId}/accesses`));
    } catch {
      setAcessos([]);
    }
  }

  if (!aberto) {
    return (
      <button
        type="button"
        onClick={() => void abrir()}
        className="text-xs text-slate-500 underline-offset-4 hover:text-slate-700 hover:underline"
      >
        quem baixou?
      </button>
    );
  }

  return (
    <div className="w-full rounded-lg border border-slate-200 bg-slate-50/60 p-2 text-xs">
      {acessos === null ? (
        <span className="text-slate-500">Carregando…</span>
      ) : acessos.length === 0 ? (
        <span className="text-slate-600">Ninguém baixou este arquivo ainda.</span>
      ) : (
        <ul className="space-y-0.5">
          {acessos.map((acesso) => (
            <li key={acesso.id} className="text-slate-600">
              {formatDateTime(acesso.at)} — {acesso.userName ?? 'usuário removido'}
              {acesso.userEmail && ` (${acesso.userEmail})`}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
